'use client';

import * as React from 'react';
import { Building2, Factory, Scale } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
  SDGFlipCard,
  cmsCardToSlide,
  type SDGSlide,
} from '@/components/sections/Sustainability/SustainabilitySDGsSection';
import { stripHtml } from '@/lib/homepage-mappers';
import type { SustainabilityColorfulCardWrapper } from '@/types/sustainability';

interface SustainabilityGovernanceSDGStaticSectionProps {
  locale?: string;
  cardWrapper?: SustainabilityColorfulCardWrapper | null;
}

export default function SustainabilityGovernanceSDGStaticSection({
  cardWrapper,
}: SustainabilityGovernanceSDGStaticSectionProps) {
  const t = useTranslations('sustainability.pages.governance');

  const fallbackSlides: SDGSlide[] = React.useMemo(
    () => [
      {
        id: 'gov-sdg-11',
        number: 11,
        titleLine: t('governanceSdg1Title').toUpperCase(),
        imageSrc: '/images/stand_env2.png',
        Icon: Building2,
        backTitle: t('governanceSdg1BackTitle'),
        backDescription: [
          t('governanceSdg1Body'),
          t('governanceSdg1BackPoint1'),
          t('governanceSdg1BackPoint2'),
        ]
          .filter(Boolean)
          .join(' '),
      },
      {
        id: 'gov-sdg-16',
        number: 16,
        titleLine: t('governanceSdg2Title').toUpperCase(),
        imageSrc: '/images/stand_gov2.png',
        Icon: Scale,
        backTitle: t('governanceSdg2BackTitle'),
        backDescription: [
          t('governanceSdg2Body'),
          t('governanceSdg2BackPoint1'),
          t('governanceSdg2BackPoint2'),
        ]
          .filter(Boolean)
          .join(' '),
      },
      {
        id: 'gov-sdg-9',
        number: 9,
        titleLine: t('governanceSdg3Title').toUpperCase(),
        imageSrc: '/images/stand_img1.jpg',
        Icon: Factory,
        backTitle: t('governanceSdg3BackTitle'),
        backDescription: [
          t('governanceSdg3Body'),
          t('governanceSdg3BackPoint1'),
          t('governanceSdg3BackPoint2'),
        ]
          .filter(Boolean)
          .join(' '),
      },
    ],
    [t],
  );

  const cmsCards = React.useMemo(
    () => [...(cardWrapper?.cardsRow1 ?? []), ...(cardWrapper?.cardRow2 ?? [])],
    [cardWrapper],
  );

  const slides: SDGSlide[] = cmsCards.length > 0 ? cmsCards.map(cmsCardToSlide) : fallbackSlides;

  const heading = cardWrapper?.title?.trim() || t('governanceSdgHeading');
  const intro = stripHtml(cardWrapper?.description) || t('governanceSdgIntro');

  return (
    <section className="bg-black text-white">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-[2.35rem]">
            {heading}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-neutral-300 sm:mt-5 sm:text-base md:text-lg">
            {intro}
          </p>
        </div>

        <div className="mx-auto mt-8 grid max-w-6xl grid-cols-1 gap-4 sm:grid-cols-2 md:mt-12 md:grid-cols-3 md:gap-5">
          {slides.map((slide) => (
            <SDGFlipCard
              key={slide.id}
              slide={slide}
              onHoverDelta={() => {}}
              flipInnerHeightClassName="h-[390px] max-sm:h-[425px] sm:h-[415px] md:h-[435px]"
            />
          ))}
        </div>
      </div>
    </section>
  );
}
